/**
 * Syncfusion (EJ2) bilesenlerinin dili - tek kurulum noktasi.
 *
 * SORUN: setCulture('tr') hem App.vue hem CFMGridPage.ts icinde KOSULSUZ
 * cagriliyordu. Uygulama dili English secilse bile EJ2 kulturu 'tr' kaldigi icin
 * grid filtre menusu Turkce geliyordu: 'Eşit' (grid.Equal), 'Bir Tarih Seçin'
 * (grid.ChooseDate), 'Filtrele'/'Temizle' (grid.FilterButton/ClearButton) ve
 * grupla-birak alani. Kultur artik kullanicinin secili dilinden turetiliyor.
 *
 * Ayrica ayni iki satirlik Turkce duzeltme ('Filtrele'/'Temizle') dokuz ayri
 * ekranda tekrarlaniyordu; hepsi buradaki OVERRIDES'a toplandi.
 *
 * YUKLEME SIRASI ONEMLIDIR ve mevcut davranisi korumak icin bilincli olarak
 * boyle: once uygulama sozlukleri, sonra EJ2 paketi, en sonda CFM duzeltmeleri.
 * languages/turkish.ts icinde Syncfusion'in kendi 'grid' ve 'pager' locale
 * nesnelerinin bir kopyasi duruyor; bugun EJ2 paketi onlarin uzerine yazdigi
 * icin ekranda EJ2'nin metinleri gorunuyor. Sirayi degistirmek, ilgisiz bir
 * suru metni sessizce degistirirdi - o ayri bir istin konusu.
 */
import { L10n, setCulture, loadCldr } from '@syncfusion/ej2-base';
import EJ2_LOCALE_TR from '@syncfusion/ej2-locale/src/tr.json';
import appLanguages from '@/languages/index';

import numberingSystems from 'cldr-data/supplemental/numberingSystems.json';
import weekData from 'cldr-data/supplemental/weekData.json';
import trGregorian from 'cldr-data/main/tr/ca-gregorian.json';
import trNumbers from 'cldr-data/main/tr/numbers.json';
import trTimeZoneNames from 'cldr-data/main/tr/timeZoneNames.json';

/**
 * Uygulama dili (Application.Language.Culture) -> EJ2 kulturu.
 * ej2-locale paketi Turkce'yi 'tr' anahtariyla veriyor, 'tr-TR' ile degil;
 * yuklenen CLDR verisi de 'tr' kulturune yaziliyor. Bu yuzden esleme sart.
 */
const CULTURE_BY_LANGUAGE = {
  'tr-TR': 'tr',
  tr: 'tr',
  'en-US': 'en-US',
  en: 'en-US',
};

const DEFAULT_LANGUAGE = 'tr-TR';

/**
 * Stok ej2-locale Turkcesinin duzeltmeleri. Yorumlardaki "stok", paketten
 * gelen ve begenilmedigi icin ezilen degerdir.
 */
const TR_OVERRIDES = {
  grid: {
    FilterButton: 'Filtrele', // stok: 'filtre'
    ClearButton: 'Temizle', // stok: 'Açık'
    FilterTrue: 'Aktif', // stok: 'Doğru' - kolon IsActive oldugu icin
    FilterFalse: 'İnaktif', // stok: 'Yanlış'
  },
  daterangepicker: {
    applyText: 'Tamam', // stok: 'Uygulamak'
    cancelText: 'İptal', // stok: 'İptal etmek'
    selectedDays: 'Tarih aralığı seçiniz', // stok: 'Seçilen Günler'
    days: 'gün seçildi', // stok: 'günler'
  },
};

/**
 * Ingilizce'de yalnizca uygulamaya OZGU anlam ezilir; geri kalan metinler
 * Syncfusion'in gomulu Ingilizcesinden gelir. 'en-US' EJ2'nin varsayilan
 * kulturu oldugu icin ne locale paketi ne de CLDR verisi yuklemek gerekir
 * (bkz. IntlBase.defaultObject). L10n, locale nesnesinde bulamadigi anahtari
 * bilesenin kendi varsayilanina dusurdugu icin bu kismi ezme guvenlidir.
 */
const EN_OVERRIDES = {
  grid: {
    FilterTrue: 'Active', // stok: 'True'
    FilterFalse: 'Inactive', // stok: 'False'
  },
};

let localeLoaded = false;

function currentLanguage() {
  return localStorage.getItem('vue-lang') || DEFAULT_LANGUAGE;
}

/** Verilen uygulama dilinin EJ2 kulturu; taninmayan dilde varsayilana duser. */
export function syncfusionCultureOf(language) {
  return CULTURE_BY_LANGUAGE[language] || CULTURE_BY_LANGUAGE[DEFAULT_LANGUAGE];
}

/** EJ2 kulturunu secili dile ayarlar. Dil verilmezse localStorage'dan okunur. */
export function applySyncfusionCulture(language) {
  setCulture(syncfusionCultureOf(language || currentLanguage()));
}

/**
 * Sozlukleri bir kez yukler ve kulturu ayarlar. Birden fazla yerden cagrilmasi
 * guvenlidir; yukleme tekrarlanmaz, kultur her cagrida tazelenir.
 *
 * Kultur modul yuklenirken secilir: dil degistirici (Header.js) tam sayfa
 * yeniden yukleme yaptigi icin bu yeterli. Yeniden yukleme olmadan dil
 * degistirilecek olursa applySyncfusionCulture() elle cagrilmalidir.
 */
export function initSyncfusionLocale() {
  if (!localeLoaded) {
    localeLoaded = true;
    loadCldr(numberingSystems, weekData, trGregorian, trNumbers, trTimeZoneNames);
    L10n.load(appLanguages);
    L10n.load({ tr: EJ2_LOCALE_TR.tr });
    L10n.load({ tr: TR_OVERRIDES, 'en-US': EN_OVERRIDES });
  }
  applySyncfusionCulture();
}

export default initSyncfusionLocale;
